import { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useCentrifuge } from '../hooks/useCentrifuge'
import { api } from '../api'
import { registerPush } from '../firebase'


export default function ChatPage() {
  const { user, logout, updateUser } = useAuth()
  const [conversations, setConversations] = useState([])
  const [activeConv, setActiveConv] = useState(null)
  const [messages, setMessages] = useState([])
  const [partnerLastReadAt, setPartnerLastReadAt] = useState(null)
  const [input, setInput] = useState('')
  const [searchQ, setSearchQ] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [pendingUsers, setPendingUsers] = useState([])
  const [showPending, setShowPending] = useState(false)
  const [editingMemo, setEditingMemo] = useState(false)
  const [memoInput, setMemoInput] = useState('')
  const [editingMyName, setEditingMyName] = useState(false)
  const [myNameInput, setMyNameInput] = useState('')
  const myNameInputRef = useRef(null)
  const [hoveredMsg, setHoveredMsg] = useState(null)
  const [forwardingMsg, setForwardingMsg] = useState(null)  // tin đang chuyển tiếp
  const [convMenuId, setConvMenuId] = useState(null)        // id conv đang mở menu ⋮
  const [otpRequests, setOtpRequests] = useState([])        // thiết bị mới đang chờ OTP
  const [showDevices, setShowDevices] = useState(false)     // panel quản lý thiết bị
  const [devices, setDevices] = useState([])
  const [showAutoFwd, setShowAutoFwd] = useState(false)     // panel tự động forward
  const [autoFwdRules, setAutoFwdRules] = useState([])
  const [allUsers, setAllUsers] = useState([])              // danh sách user để chọn
  const [fwdSourceIds, setFwdSourceIds] = useState([])      // user đầu vào đang chọn
  const [fwdTargetId, setFwdTargetId] = useState('')        // user đầu ra đang chọn
  const [autoReplyConv, setAutoReplyConv] = useState(null)  // conv đang mở modal auto reply
  const [autoReplyContent, setAutoReplyContent] = useState('Ok')
  const [autoReplyActive, setAutoReplyActive] = useState(true)
  const [autoReplyMap, setAutoReplyMap] = useState({})      // convId → is_active
  const [incomingRing, setIncomingRing] = useState(null)    // { callerName, conversationId }
  const [callState, setCallState] = useState(null)          // null | 'calling' | 'active'
  const [callPartnerName, setCallPartnerName] = useState('')
  const [callTimer, setCallTimer] = useState(0)
  const ringAudioRef = useRef(null)
  const pcRef = useRef(null)
  const localStreamRef = useRef(null)
  const remoteStreamRef = useRef(null)
  const remoteAudioRef = useRef(null)
  const callConvIdRef = useRef(null)
  const pendingIceRef = useRef([])
  const callTimerIntervalRef = useRef(null)
  const callTimerValueRef = useRef(0)   // giá trị timer hiện tại (tránh stale closure)
  const isCallerRef = useRef(false)     // true nếu mình là người gọi đi
  const [replyTo, setReplyTo] = useState(null)  // tin nhắn đang được reply
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640)
  const [chatMenuOpen, setChatMenuOpen] = useState(false)
  const [autoDeleteSub, setAutoDeleteSub] = useState(false)
  const [autoDeleteDays, setAutoDeleteDays] = useState(null) // null | 1 | 2
  const [chatFontSize, setChatFontSize] = useState(() => parseInt(localStorage.getItem('tchat_font_size') || '14', 10))
  const [showFontPicker, setShowFontPicker] = useState(false)
  const bottomRef = useRef(null)
  const fileInputRef = useRef(null)
  const chatInputRef = useRef(null)
  const composingRef = useRef(false)  // đang IME composition (Android/iOS)
  const memoInputRef = useRef(null)

  // Ref cập nhật ngay khi render — không bao giờ bị stale như module-level var hay useEffect
  const activeConvIdRef = useRef(null)
  activeConvIdRef.current = activeConv?.id || null
  const callStateRef = useRef(null)
  callStateRef.current = callState
  const userRef = useRef(null)
  userRef.current = user

  // --- Ring sound dùng file audio ---
  const startRingSound = useCallback(() => {
    try {
      const audio = new Audio('/sounds/ring.wav')
      audio.loop = true
      audio.volume = 1.0
      ringAudioRef.current = audio
      audio.play().catch(() => {})
    } catch {}
  }, [])

  const stopRingSound = useCallback(() => {
    if (ringAudioRef.current) {
      ringAudioRef.current.pause()
      ringAudioRef.current.currentTime = 0
      ringAudioRef.current = null
    }
  }, [])

  // Âm thanh thông báo tin nhắn mới
  const playNotificationSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const t = ctx.currentTime
      // Nốt đôi: ping cao → ping thấp hơn
      ;[[880, 0, 0.12], [660, 0.15, 0.25]].forEach(([freq, start, end]) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain); gain.connect(ctx.destination)
        osc.type = 'sine'
        osc.frequency.value = freq
        gain.gain.setValueAtTime(0, t + start)
        gain.gain.linearRampToValueAtTime(0.6, t + start + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.001, t + end)
        osc.start(t + start); osc.stop(t + end)
      })
    } catch {}
  }, [])

  // Detect mobile / resize
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Heartbeat mỗi 30s để tracking online status
  useEffect(() => {
    api.heartbeat()
    const timer = setInterval(api.heartbeat, 30_000)
    return () => clearInterval(timer)
  }, [])

  // Admin: load danh sách chờ duyệt mỗi 30s
  useEffect(() => {
    if (!user?.is_admin) return
    function loadPending() {
      api.getPendingUsers().then(({ users }) => setPendingUsers(users)).catch(() => {})
    }
    loadPending()
    const timer = setInterval(loadPending, 30_000)
    return () => clearInterval(timer)
  }, [user?.is_admin])

  // Xử lý publication từ Centrifugo
  const onMessage = useCallback((channel, data) => {
    const convId = channel.split(':')[1]
    const currentId = String(activeConvIdRef.current ?? '')  // so sánh dưới dạng string tránh type mismatch

    if (data.event === 'message') {
      const msg = data
      if (currentId === convId) {
        setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg])
      }
      // Phát âm thanh khi nhận tin từ người khác
      if (msg.sender_id !== userRef.current?.id) {
        playNotificationSound()
      }
      setConversations(prev => prev.map(c =>
        String(c.id) === convId ? { ...c, last_message: msg.content, unread_count: currentId === convId ? 0 : (c.unread_count || 0) + 1 } : c
      ))
    } else if (data.event === 'ring') {
      // Bỏ qua event cũ từ Centrifugo history (quá 30 giây)
      if (data.sent_at && Date.now() - data.sent_at > 30_000) return
      if (data.caller_id !== user?.id) {
        setIncomingRing({ callerName: data.caller_name, conversationId: convId })
        startRingSound()
      }
    } else if (data.event === 'ring_cancel') {
      setIncomingRing(null)
      stopRingSound()
      // Cleanup WebRTC phía nhận — KHÔNG gửi ring_cancel lại để tránh vòng lặp
      localStreamRef.current?.getTracks().forEach(t => t.stop())
      localStreamRef.current = null
      remoteStreamRef.current = null
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null
      pcRef.current?.close()
      pcRef.current = null
      callConvIdRef.current = null
      pendingIceRef.current = []
      if (callTimerIntervalRef.current) { clearInterval(callTimerIntervalRef.current); callTimerIntervalRef.current = null }
      callTimerValueRef.current = 0
      setCallState(null)
      setCallTimer(0)
    } else if (data.event === 'webrtc_accept') {
      // Caller nhận được: callee đã chấp nhận → tạo và gửi offer
      if (data.from_user_id !== user?.id && pcRef.current) {
        ;(async () => {
          try {
            const offer = await pcRef.current.createOffer()
            await pcRef.current.setLocalDescription(offer)
            await api.webrtcSignal(convId, 'webrtc_offer', { sdp: offer })
            stopRingSound()
            setCallState('active')
            callTimerValueRef.current = 0
            setCallTimer(0)
            if (callTimerIntervalRef.current) clearInterval(callTimerIntervalRef.current)
            callTimerIntervalRef.current = setInterval(() => {
              callTimerValueRef.current += 1
              setCallTimer(callTimerValueRef.current)
            }, 1000)
          } catch (err) { console.error('webrtc_accept handler lỗi', err) }
        })()
      }
    } else if (data.event === 'webrtc_offer') {
      // Callee nhận offer → set remote desc, tạo answer
      if (data.from_user_id !== user?.id && pcRef.current) {
        ;(async () => {
          try {
            await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp))
            for (const c of pendingIceRef.current) {
              await pcRef.current.addIceCandidate(new RTCIceCandidate(c)).catch(() => {})
            }
            pendingIceRef.current = []
            const answer = await pcRef.current.createAnswer()
            await pcRef.current.setLocalDescription(answer)
            await api.webrtcSignal(convId, 'webrtc_answer', { sdp: answer })
            stopRingSound()
            setCallState('active')
            callTimerValueRef.current = 0
            setCallTimer(0)
            if (callTimerIntervalRef.current) clearInterval(callTimerIntervalRef.current)
            callTimerIntervalRef.current = setInterval(() => {
              callTimerValueRef.current += 1
              setCallTimer(callTimerValueRef.current)
            }, 1000)
          } catch (err) { console.error('webrtc_offer handler lỗi', err) }
        })()
      }
    } else if (data.event === 'webrtc_answer') {
      // Caller nhận answer → set remote desc
      if (data.from_user_id !== user?.id && pcRef.current) {
        pcRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp)).catch(console.error)
      }
    } else if (data.event === 'webrtc_ice') {
      // Nhận ICE candidate từ đối phương
      if (data.from_user_id !== user?.id) {
        if (pcRef.current?.remoteDescription) {
          pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {})
        } else {
          pendingIceRef.current.push(data.candidate)
        }
      }
    } else if (data.event === 'reaction') {
      setMessages(prev => prev.map(m =>
        m.id === data.message_id ? { ...m, reactions: data.reactions } : m
      ))
    } else if (data.event === 'recall') {
      setMessages(prev => prev.map(m =>
        m.id === data.message_id ? { ...m, is_recalled: true } : m
      ))
    } else if (data.event === 'otp_request') {
      if (data.sent_at && Date.now() - data.sent_at > 300_000) return // bỏ qua nếu cũ hơn 5 phút
      setOtpRequests(prev => {
        if (prev.find(r => r.device_token === data.device_token)) return prev
        return [...prev, data]
      })
    } else if (data.event === 'read') {
      // Chỉ cập nhật partnerLastReadAt khi đối phương (không phải mình) đọc
      if (currentId === convId && data.user_id !== user?.id) {
        setPartnerLastReadAt(data.read_at)
      }
      if (data.user_id !== user?.id) {
        setConversations(prev => prev.map(c =>
          c.id === convId ? { ...c, unread_count: 0 } : c
        ))
      }
    }
  }, [user?.id, startRingSound, stopRingSound, playNotificationSound])

  const { subscribe, unsubscribe } = useCentrifuge(onMessage)

  const subscribeAll = useCallback((convList) => {
    convList.forEach(c => subscribe(`dm:${c.id}`))
  }, [subscribe])

  // Admin: subscribe channel nhận thông báo hệ thống
  useEffect(() => {
    if (!user?.is_admin) return
    subscribe('admin:notifications')
  }, [user?.is_admin, subscribe])

  // Admin: poll OTP đang chờ từ DB mỗi 10s (bù trường hợp bỏ lỡ realtime)
  useEffect(() => {
    if (!user?.is_admin) return
    function loadOtps() {
      api.getPendingOtps().then(({ otps }) => {
        setOtpRequests(otps.map(o => ({
          device_token: o.device_token,
          device_name: o.device_name,
          otp_code: o.otp_code,
          username: o.username,
          display_name: o.display_name,
          sent_at: new Date(o.created_at).getTime(),
        })))
      }).catch(() => {})
    }
    loadOtps()
    const timer = setInterval(loadOtps, 10_000)
    return () => clearInterval(timer)
  }, [user?.is_admin])

  useEffect(() => {
    async function loadConvs() {
      const { conversations: convs } = await api.listConversations().catch(() => ({ conversations: [] }))
      setConversations(convs)
      subscribeAll(convs)
      return convs
    }

    async function init() {
      const convs = await loadConvs()
      // Restore conversation từ URL hash khi refresh
      const hashId = window.location.hash.replace('#', '')
      if (hashId) {
        const saved = convs.find(c => String(c.id) === hashId)
        if (saved) setActiveConv(saved)
      }
      // User thường: tự động tạo DM với admin nếu chưa có
      if (!user?.is_admin) {
        try {
          const { user: admin } = await api.getAdmin()
          const hasAdminConv = convs.some(c => c.partner?.id === admin.id)
          if (!hasAdminConv) {
            await api.createDirect(admin.id)
            await loadConvs()
          }
        } catch {}
      }
    }

    init()

    // Đăng ký push notification
    registerPush().then(token => {
      if (token) api.saveFcmToken(token).catch(() => {})
    })

    const timer = setInterval(loadConvs, 10_000)
    return () => clearInterval(timer)
  }, [subscribeAll, user?.is_admin])

  // Khi chọn conversation
  useEffect(() => {
    if (!activeConv) return
    setChatMenuOpen(false)
    setAutoDeleteSub(false)
    setAutoDeleteDays(activeConv.auto_delete_days ?? null)
    setMessages([])
    setPartnerLastReadAt(null)
    api.getMessages(activeConv.id).then(({ messages, partner_last_read_at }) => {
      setMessages(messages)
      setPartnerLastReadAt(partner_last_read_at)
    }).catch(() => {})
    // Đánh dấu đã đọc
    api.markRead(activeConv.id).catch(() => {})
    // Reset unread badge
    setConversations(prev => prev.map(c => c.id === activeConv.id ? { ...c, unread_count: 0 } : c))
  }, [activeConv])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Tìm user debounce 300ms
  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return }
    const timer = setTimeout(() => {
      api.searchUsers(searchQ.trim())
        .then(({ users }) => setSearchResults(users))
        .catch(() => setSearchResults([]))
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQ])

  // Sync remote audio stream khi callState chuyển sang 'active'
  useEffect(() => {
    if (callState === 'active' && remoteAudioRef.current && remoteStreamRef.current) {
      remoteAudioRef.current.srcObject = remoteStreamRef.current
    }
  }, [callState])

  async function openDm(targetUserId) {
    setError('')
    setSearchQ('')
    setSearchResults([])
    try {
      const { conversation_id } = await api.createDirect(targetUserId)
      const { conversations: convs } = await api.listConversations()
      setConversations(convs)
      subscribeAll(convs)
      const conv = convs.find(c => c.id === conversation_id)
      if (conv) { setActiveConv(conv); window.location.hash = conv.id }
    } catch (err) {
      setError(err.message)
    }
  }

  function handleInputChange(e) {
    setInput(e.target.value)
  }

  async function sendMessage(e) {
    e.preventDefault()
    if (!input.trim() || !activeConv) return
    const text = input.trim()
    const replyId = replyTo?.id || null
    setInput('')
    setReplyTo(null)
    setError('')
    try {
      const { message } = await api.sendMessage(activeConv.id, text, replyId ? { reply_to_id: replyId } : {})
      setMessages(prev => prev.find(m => m.id === message.id) ? prev : [...prev, message])
    } catch (err) {
      setError(err.message)
    }
  }

  async function sendImage(e) {
    const file = e.target.files?.[0]
    if (!file || !activeConv) return
    e.target.value = ''
    setUploading(true)
    setError('')
    try {
      const { file_url, file_name, type } = await api.uploadFile(file)
      const { message } = await api.sendMessage(activeConv.id, file_name, { type, file_url, file_name })
      setMessages(prev => prev.find(m => m.id === message.id) ? prev : [...prev, message])
    } catch (err) {
      setError(err.message)
    } finally {
      setUploading(false)
    }
  }

  function createPC(convId) {
    if (pcRef.current) pcRef.current.close()
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ],
    })
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        api.webrtcSignal(convId, 'webrtc_ice', { candidate: e.candidate }).catch(() => {})
      }
    }
    pc.ontrack = (e) => {
      remoteStreamRef.current = e.streams[0]
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = e.streams[0]
    }
    pc.onconnectionstatechange = () => {
      if (['disconnected', 'failed'].includes(pc.connectionState)) hangUp()
    }
    pcRef.current = pc
    return pc
  }

  async function hangUp() {
    stopRingSound()
    const convId = callConvIdRef.current
    const currentState = callStateRef.current
    const duration = callTimerValueRef.current
    localStreamRef.current?.getTracks().forEach(t => t.stop())
    localStreamRef.current = null
    remoteStreamRef.current = null
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null
    pcRef.current?.close()
    pcRef.current = null
    callConvIdRef.current = null
    pendingIceRef.current = []
    if (callTimerIntervalRef.current) { clearInterval(callTimerIntervalRef.current); callTimerIntervalRef.current = null }
    callTimerValueRef.current = 0
    setCallState(null)
    setCallTimer(0)
    if (convId) {
      // Xác định kết quả để lưu vào lịch sử cuộc gọi
      let callResult = null
      if (currentState === 'active') {
        callResult = 'ended'
      } else if (currentState === 'calling' && isCallerRef.current) {
        callResult = 'missed'  // caller chủ động hủy hoặc timeout
      }
      await api.ringCancel(convId, callResult, duration).catch(() => {})
    }
  }

  async function callPartner() {
    if (!activeConv) return
    callConvIdRef.current = activeConv.id
    isCallerRef.current = true
    setCallPartnerName(partnerLabel(activeConv.partner))
    setCallState('calling')
    startRingSound()
    try {
      const pc = createPC(activeConv.id)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      localStreamRef.current = stream
      stream.getTracks().forEach(t => pc.addTrack(t, stream))
      await api.ring(activeConv.id)
      // Tự động hủy sau 30s nếu chưa kết nối → lưu cuộc gọi nhỡ
      setTimeout(() => {
        if (pcRef.current && callStateRef.current !== 'active') hangUp()
      }, 30_000)
    } catch (err) {
      hangUp()
      setError(err.message || 'Không thể truy cập microphone')
    }
  }

  async function cancelRing() {
    hangUp()
  }

  async function acceptRing() {
    stopRingSound()
    const convId = incomingRing?.conversationId
    const callerName = incomingRing?.callerName
    setIncomingRing(null)
    if (!convId) return
    callConvIdRef.current = convId
    isCallerRef.current = false
    setCallPartnerName(callerName || '')
    setCallState('calling')
    try {
      const pc = createPC(convId)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      localStreamRef.current = stream
      stream.getTracks().forEach(t => pc.addTrack(t, stream))
      // Thông báo caller rằng mình đã chấp nhận và sẵn sàng nhận offer
      await api.webrtcSignal(convId, 'webrtc_accept', {})
    } catch (err) {
      hangUp()
      setError(err.message || 'Không thể truy cập microphone')
    }
  }

  async function declineRing() {
    stopRingSound()
    const convId = incomingRing?.conversationId
    setIncomingRing(null)
    // Lưu "bị từ chối" vào lịch sử (callee từ chối)
    if (convId) await api.ringCancel(convId, 'declined', 0).catch(() => {})
  }

  function formatTimer(s) {
    const m = Math.floor(s / 60)
    return `${m}:${String(s % 60).padStart(2, '0')}`
  }

  function formatConvTime(dateStr) {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    const now = new Date()
    const same = (a, b) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
    if (same(d, now)) return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
    const diffDays = Math.floor((now - d) / 86400000)
    if (diffDays < 7) {
      const DOW = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7']
      return DOW[d.getDay()]
    }
    return `${d.getDate()}/${d.getMonth() + 1}`
  }

  function formatDateLabel(dateStr) {
    const d = new Date(dateStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    const same = (a, b) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
    const DOW = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy']
    const dow = DOW[d.getDay()]
    const full = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`
    if (same(d, today)) return `Hôm nay - ${dow} - ${full}`
    if (same(d, yesterday)) return `Hôm qua - ${dow} - ${full}`
    return `${dow} - ${full}`
  }

  function renderText(content) {
    const lines = content.split('\n')
    return lines.map((line, i) => {
      if (line.startsWith('> ')) {
        return (
          <div key={i} style={styles.quoteLine}>
            <span style={styles.quoteBar} />
            <span style={styles.quoteText}>{line.slice(2)}</span>
          </div>
        )
      }
      return <div key={i} style={i === 0 ? {} : { marginTop: 2 }}>{line || ' '}</div>
    })
  }

  function copyText(text) {
    navigator.clipboard.writeText(text).catch(() => {})
  }

  function downloadFile(url, name) {
    const a = document.createElement('a')
    a.href = api.fileUrl(url)
    a.download = name || 'file'
    a.target = '_blank'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  async function forwardMessage(targetConvId) {
    if (!forwardingMsg) return
    setForwardingMsg(null)
    try {
      const extra = forwardingMsg.type !== 'text'
        ? { type: forwardingMsg.type, file_url: forwardingMsg.file_url, file_name: forwardingMsg.file_name }
        : {}
      await api.sendMessage(targetConvId, forwardingMsg.content, extra)
    } catch (err) {
      setError(err.message)
    }
  }

  async function toggleReaction(msg, emoji) {
    try {
      const { reactions } = await api.reactMessage(activeConv.id, msg.id, emoji)
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, reactions } : m))
    } catch (err) {
      setError(err.message)
    }
  }

  async function recallMessage(msg) {
    try {
      await api.recallMessage(activeConv.id, msg.id)
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_recalled: true } : m))
    } catch (err) {
      setError(err.message)
    }
  }

  async function deleteMessageForMe(msg) {
    try {
      await api.deleteMessage(activeConv.id, msg.id)
      setMessages(prev => prev.filter(m => m.id !== msg.id))
    } catch (err) {
      setError(err.message)
    }
  }

  async function saveMyName(e) {
    e?.preventDefault()
    const name = myNameInput.trim()
    if (!name) return
    setEditingMyName(false)
    try {
      await api.updateMyName(name)
      updateUser({ display_name: name })
    } catch (err) {
      setError(err.message)
    }
  }

  function changeFontSize(size) {
    setChatFontSize(size)
    localStorage.setItem('tchat_font_size', String(size))
    setShowFontPicker(false)
  }

  function startEditMyName() {
    setMyNameInput(user?.display_name || '')
    setEditingMyName(true)
    setTimeout(() => myNameInputRef.current?.focus(), 50)
  }

  async function saveMemo(e) {
    e?.preventDefault()
    const memo = memoInput.trim()
    const partnerId = activeConv?.partner?.id
    if (!partnerId) return
    setEditingMemo(false)
    await api.setMemo(partnerId, memo).catch(() => {})
    // Cập nhật local state ngay không cần reload
    setConversations(prev => prev.map(c =>
      c.id === activeConv.id ? { ...c, partner: { ...c.partner, memo } } : c
    ))
    setActiveConv(prev => ({ ...prev, partner: { ...prev.partner, memo } }))
  }

  function startEditMemo() {
    setMemoInput(activeConv?.partner?.memo || '')
    setEditingMemo(true)
    setTimeout(() => memoInputRef.current?.focus(), 50)
  }

  async function deleteConversation(convId) {
    setConvMenuId(null)
    if (!window.confirm('Xóa toàn bộ đoạn chat này?')) return
    try {
      await api.deleteConversation(convId)
      setConversations(prev => prev.filter(c => c.id !== convId))
      if (activeConv?.id === convId) { setActiveConv(null); window.location.hash = '' }
    } catch (err) {
      setError(err.message)
    }
  }

  async function loadDevices() {
    const { devices: list } = await api.getDevices().catch(() => ({ devices: [] }))
    setDevices(list)
  }

  async function revokeDevice(id) {
    await api.revokeDevice(id).catch(() => {})
    setDevices(prev => prev.filter(d => d.id !== id))
  }

  async function loadAutoFwd() {
    const { rules } = await api.getAutoForwardRules().catch(() => ({ rules: [] }))
    setAutoFwdRules(rules)
    // Load danh sách tất cả user (tái dùng từ admin endpoint)
    const { users } = await api.searchUsers('a').catch(() => ({ users: [] }))
    // Lấy thêm nếu cần — dùng admin/users endpoint
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('tchat_token')}` },
      })
      const d = await r.json()
      if (d.users) setAllUsers(d.users.filter(u => u.is_active))
    } catch {}
  }

  async function saveAutoFwdRule() {
    if (fwdSourceIds.length === 0 || !fwdTargetId) return
    await api.createAutoForwardRules(fwdSourceIds, fwdTargetId).catch(() => {})
    setFwdSourceIds([])
    setFwdTargetId('')
    loadAutoFwd()
  }

  async function toggleAutoFwdRule(id) {
    await api.toggleAutoForwardRule(id).catch(() => {})
    setAutoFwdRules(prev => prev.map(r => r.id === id ? { ...r, is_active: !r.is_active } : r))
  }

  async function deleteAutoFwdRule(id) {
    await api.deleteAutoForwardRule(id).catch(() => {})
    setAutoFwdRules(prev => prev.filter(r => r.id !== id))
  }

  function toggleFwdSource(uid) {
    setFwdSourceIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid])
  }

  async function openAutoReply(conv) {
    setConvMenuId(null)
    const { rule } = await api.getAutoReply(conv.id).catch(() => ({ rule: null }))
    setAutoReplyContent(rule?.reply_content || 'Ok')
    setAutoReplyActive(rule?.is_active ?? true)
    setAutoReplyConv(conv)
  }

  async function saveAutoReply() {
    if (!autoReplyConv) return
    await api.setAutoReply(autoReplyConv.id, autoReplyContent, autoReplyActive)
    setAutoReplyMap(prev => ({ ...prev, [autoReplyConv.id]: autoReplyActive }))
    setAutoReplyConv(null)
  }

  async function clearAllMessages() {
    if (!activeConv) return
    if (!window.confirm('Xóa hết nội dung tin nhắn trong đoạn chat này?')) return
    setChatMenuOpen(false)
    try {
      await api.clearMessages(activeConv.id)
      setMessages([])
    } catch (err) {
      setError(err.message)
    }
  }

  async function changeAutoDelete(days) {
    if (!activeConv) return
    setChatMenuOpen(false)
    setAutoDeleteSub(false)
    try {
      await api.setAutoDelete(activeConv.id, days)
      setAutoDeleteDays(days)
      setConversations(prev => prev.map(c =>
        c.id === activeConv.id ? { ...c, auto_delete_days: days } : c
      ))
    } catch (err) {
      setError(err.message)
    }
  }

  async function approveUser(id) {
    await api.approveUser(id)
    setPendingUsers(prev => prev.filter(u => u.id !== id))
  }

  async function rejectUser(id) {
    if (!window.confirm('Xóa tài khoản này?')) return
    await api.rejectUser(id)
    setPendingUsers(prev => prev.filter(u => u.id !== id))
  }

  const myName = user?.display_name || user?.username
  const myNameLabel = myName ? `${myName} (Tôi)` : ''

  const AVATAR_COLORS = ['#e74c3c','#e67e22','#16a34a','#0891b2','#7c3aed','#db2777','#d97706','#059669','#2563eb','#9333ea','#c2410c','#0f766e']
  function getAvatarColor(name) {
    if (!name) return '#4f46e5'
    let h = 0
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h)
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
  }
  function getInitials(name) {
    if (!name) return '?'
    const words = name.trim().split(/\s+/)
    if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase()
    return name.slice(0, 2).toUpperCase()
  }

  function formatLastSeen(partner) {
    if (!partner) return ''
    if (partner.is_online) return 'Đang online'
    if (!partner.last_seen_at) return ''
    const diff = Math.floor((Date.now() - new Date(partner.last_seen_at)) / 1000)
    if (diff < 60) return 'Vừa mới truy cập'
    if (diff < 3600) return `Truy cập ${Math.floor(diff / 60)} phút trước`
    if (diff < 86400) return `Truy cập ${Math.floor(diff / 3600)} giờ trước`
    if (diff < 604800) return `Truy cập ${Math.floor(diff / 86400)} ngày trước`
    return `Truy cập ${new Date(partner.last_seen_at).toLocaleDateString('vi-VN')}`
  }

  // Tên hiển thị của đối phương, kèm memo nếu có
  function partnerLabel(partner) {
    if (!partner) return ''
    const name = partner.display_name || partner.username
    return partner.memo ? `${name} (${partner.memo})` : name
  }

  return (
    <div style={styles.layout}>
      {/* Sidebar — trên mobile chỉ hiện khi chưa chọn conversation */}
      <aside style={{
        ...styles.sidebar,
        ...(isMobile ? {
          display: activeConv ? 'none' : 'flex',
          width: '100%',
        } : {}),
      }}>
        <div style={styles.sidebarHeader}>
          <span style={styles.appName}>💬 T-chat</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* Nút chọn cỡ chữ */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowFontPicker(v => !v)}
                style={{ ...styles.logoutBtn, fontSize: 13, fontWeight: 700, letterSpacing: 0.3, padding: '4px 7px' }}
                title="Cỡ chữ"
              >Aa</button>
              {showFontPicker && (
                <div style={styles.fontPickerDrop}>
                  {[{ label: 'Nhỏ', size: 12 }, { label: 'Vừa', size: 14 }, { label: 'To', size: 16 }, { label: 'Rất to', size: 18 }, { label: 'Cực to', size: 21 }].map(opt => (
                    <button
                      key={opt.size}
                      onClick={() => changeFontSize(opt.size)}
                      style={{
                        ...styles.fontPickerBtn,
                        fontSize: opt.size > 16 ? 15 : opt.size,
                        fontWeight: chatFontSize === opt.size ? 700 : 400,
                        color: chatFontSize === opt.size ? '#4f46e5' : '#e2e8f0',
                        background: chatFontSize === opt.size ? '#2d2d44' : 'none',
                      }}
                    >
                      {opt.label}
                      <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 4 }}>{opt.size}px</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          <button
            onClick={() => { if (window.confirm('Bạn muốn thoát chứ?')) logout() }}
            style={styles.logoutBtn}
            title="Exit"
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = '#2d2d44' }}
            onMouseLeave={e => { e.currentTarget.style.color = '#aaa'; e.currentTarget.style.background = 'none' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M13 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7"/>
              <polyline points="17 8 21 12 17 16"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
          </div>
        </div>
        <div style={styles.me}>
          Xin chào,{' '}
          {editingMyName ? (
            <form onSubmit={saveMyName} style={{ display: 'inline' }}>
              <input
                ref={myNameInputRef}
                value={myNameInput}
                onChange={e => setMyNameInput(e.target.value)}
                onBlur={saveMyName}
                style={styles.myNameInput}
              />
            </form>
          ) : (
            <>
              <b>{myName}</b>
              {user?.is_admin && (
                <button onClick={startEditMyName} style={styles.myNameEditBtn} title="Đổi tên hiển thị">✏️</button>
              )}
            </>
          )}
        </div>

        {/* Search — chỉ admin */}
        {user?.is_admin && (
          <div style={styles.searchWrap}>
            <input
              placeholder="Tìm username..."
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              style={styles.newDmInput}
            />
            {searchResults.length > 0 && (
              <div style={styles.searchDrop}>
                {searchResults.map(u => (
                  <div key={u.id} style={styles.searchItem} onClick={() => openDm(u.id)}>
                    <span style={styles.searchName}>
                      {u.display_name || u.username}
                      {u.is_admin && <span style={styles.adminBadge}>Admin</span>}
                    </span>
                    <span style={styles.searchSub}>@{u.username}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin: panel duyệt user */}
        {user?.is_admin && (
          <div style={styles.pendingSection}>
            <button
              style={styles.pendingToggle}
              onClick={() => setShowPending(v => !v)}
            >
              Chờ duyệt
              {pendingUsers.length > 0 && (
                <span style={styles.pendingCount}>{pendingUsers.length}</span>
              )}
              <span style={{ marginLeft: 'auto' }}>{showPending ? '▲' : '▼'}</span>
            </button>
            {showPending && (
              <div>
                {pendingUsers.length === 0 && (
                  <div style={styles.pendingEmpty}>Không có ai chờ duyệt</div>
                )}
                {pendingUsers.map(u => (
                  <div key={u.id} style={styles.pendingItem}>
                    <div>
                      <div style={styles.pendingName}>{u.display_name || u.username}</div>
                      <div style={styles.pendingSub}>@{u.username}</div>
                    </div>
                    <div style={styles.pendingActions}>
                      <button style={styles.approveBtn} onClick={() => approveUser(u.id)}>✓</button>
                      <button style={styles.rejectBtn} onClick={() => rejectUser(u.id)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin: panel quản lý thiết bị + OTP chờ */}
        {user?.is_admin && (
          <div style={styles.pendingSection}>
            <button
              style={styles.pendingToggle}
              onClick={() => { setShowDevices(v => { if (!v) loadDevices(); return !v }) }}
            >
              Thiết bị
              {otpRequests.length > 0 && (
                <span style={styles.pendingCount}>{otpRequests.length}</span>
              )}
              <span style={{ marginLeft: 'auto' }}>{showDevices ? '▲' : '▼'}</span>
            </button>
            {showDevices && (
              <div>
                {/* OTP đang chờ */}
                {otpRequests.length > 0 && (
                  <div>
                    <div style={styles.deviceSectionLabel}>🔐 Chờ xác nhận OTP</div>
                    {otpRequests.map(req => (
                      <div key={req.device_token} style={styles.otpItem}>
                        <div style={styles.otpItemBody}>
                          <div style={styles.pendingName}>{req.display_name || req.username}</div>
                          <div style={styles.pendingSub}>{req.device_name}</div>
                          <div style={styles.otpItemCode}>{req.otp_code}</div>
                          <div style={styles.otpItemHint}>Đọc mã này cho nhân viên</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Thiết bị đã đăng ký */}
                <div style={styles.deviceSectionLabel}>✅ Đã đăng ký</div>
                {devices.length === 0 && <div style={styles.pendingEmpty}>Chưa có thiết bị nào</div>}
                {devices.map(d => (
                  <div key={d.id} style={styles.pendingItem}>
                    <div>
                      <div style={styles.pendingName}>{d.display_name || d.username}</div>
                      <div style={styles.pendingSub}>{d.device_name}</div>
                      <div style={{ ...styles.pendingSub, fontSize: 10 }}>
                        Lần cuối: {new Date(d.last_seen_at).toLocaleString('vi-VN')}
                      </div>
                    </div>
                    <button style={styles.rejectBtn} onClick={() => revokeDevice(d.id)} title="Thu hồi thiết bị">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin: panel Tự động Forward */}
        {user?.is_admin && (
          <div style={styles.pendingSection}>
            <button
              style={styles.pendingToggle}
              onClick={() => { setShowAutoFwd(v => { if (!v) loadAutoFwd(); return !v }) }}
            >
              Tự động Forward
              <span style={{ marginLeft: 'auto' }}>{showAutoFwd ? '▲' : '▼'}</span>
            </button>
            {showAutoFwd && (
              <div style={{ padding: '0 0 8px' }}>
                {/* Danh sách rules hiện có */}
                {autoFwdRules.length === 0 && <div style={styles.pendingEmpty}>Chưa có rule nào</div>}
                {autoFwdRules.map(r => (
                  <div key={r.id} style={{ ...styles.pendingItem, flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: r.is_active ? '#22c55e' : '#6b7280' }}>
                        {r.source_display_name || r.source_username} → {r.target_display_name || r.target_username}
                      </span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          style={{ ...styles.approveBtn, background: r.is_active ? '#4f46e5' : '#374151', padding: '2px 8px', fontSize: 11 }}
                          onClick={() => toggleAutoFwdRule(r.id)}
                          title={r.is_active ? 'Tắt' : 'Bật'}
                        >{r.is_active ? 'Bật' : 'Tắt'}</button>
                        <button style={styles.rejectBtn} onClick={() => deleteAutoFwdRule(r.id)} title="Xóa rule">✕</button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Form thêm rule mới */}
                <div style={styles.deviceSectionLabel}>➕ Thêm rule mới</div>
                <div style={{ padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>Nguồn (nhiều user):</div>
                  <div style={{ maxHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {allUsers.filter(u => !u.is_admin).map(u => (
                      <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#e2e8f0' }}>
                        <input
                          type="checkbox"
                          checked={fwdSourceIds.includes(u.id)}
                          onChange={() => toggleFwdSource(u.id)}
                          style={{ accentColor: '#4f46e5' }}
                        />
                        {u.display_name || u.username}
                        <span style={{ fontSize: 10, color: '#6b7280' }}>@{u.username}</span>
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Đích (1 user):</div>
                  <select
                    value={fwdTargetId}
                    onChange={e => setFwdTargetId(e.target.value)}
                    style={{ background: '#1e1e3a', color: '#e2e8f0', border: '1px solid #3d3d5c', borderRadius: 6, padding: '4px 8px', fontSize: 12 }}
                  >
                    <option value="">-- Chọn user đích --</option>
                    {allUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.display_name || u.username} (@{u.username})</option>
                    ))}
                  </select>
                  <button
                    style={{ ...styles.approveBtn, width: '100%', padding: '6px', fontSize: 12, opacity: (fwdSourceIds.length === 0 || !fwdTargetId) ? 0.4 : 1 }}
                    onClick={saveAutoFwdRule}
                    disabled={fwdSourceIds.length === 0 || !fwdTargetId}
                  >
                    Lưu rule ({fwdSourceIds.length} nguồn)
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Danh sách hội thoại */}
        <div style={styles.convList} onClick={() => { setConvMenuId(null); setShowFontPicker(false) }}>
          {conversations.map(conv => {
            const isActive = activeConv?.id === conv.id
            const isOnline = conv.partner?.is_online
            const menuOpen = convMenuId === conv.id
            return (
              <div
                key={conv.id}
                style={{ ...styles.convItem, ...(isActive ? styles.convItemActive : {}), position: 'relative' }}
                onClick={() => { setActiveConv(conv); window.location.hash = conv.id; setConvMenuId(null) }}
              >
                <div style={styles.convRow}>
                  {/* Avatar tròn */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{ ...styles.convAvatar, background: getAvatarColor(partnerLabel(conv.partner) || conv.name || '') }}>
                      {getInitials(partnerLabel(conv.partner) || conv.name || '?')}
                    </div>
                    <span style={{ ...styles.onlineDotAvatar, background: isOnline ? '#22c55e' : '#4b5563' }} title={isOnline ? 'Đang online' : 'Offline'} />
                  </div>

                  {/* Nội dung */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.convNameRow}>
                      <span style={styles.convName}>
                        {partnerLabel(conv.partner) || conv.name || 'Chat'}
                      </span>
                      {autoReplyMap[conv.id] && (
                        <span style={styles.autoReplyDot} title="Auto Reply đang bật">⚡</span>
                      )}
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0, paddingLeft: 8 }}>
                        {/* Tick trạng thái — chỉ hiện nếu mình gửi tin cuối */}
                        {conv.last_message_sender_id === user?.id && (
                          <span style={{
                            fontSize: 11, fontWeight: 700,
                            color: conv.partner_last_read_at && conv.last_message_at && new Date(conv.partner_last_read_at) >= new Date(conv.last_message_at)
                              ? '#22c55e' : '#9ca3af'
                          }}>
                            {conv.partner_last_read_at && conv.last_message_at && new Date(conv.partner_last_read_at) >= new Date(conv.last_message_at) ? '✓✓' : '✓'}
                          </span>
                        )}
                        <span style={styles.convTime}>{formatConvTime(conv.last_message_at)}</span>
                      </span>
                    </div>
                    {conv.last_message && (
                      <div style={{ ...styles.convPreview, fontSize: Math.max(10, chatFontSize - 2) }}>{conv.last_message}</div>
                    )}
                  </div>

                  {/* Phải: badge + menu */}
                  <div style={styles.convRowRight}>
                    {conv.unread_count > 0 && (
                      <span style={styles.unreadBadge}>{conv.unread_count}</span>
                    )}
                    {user?.is_admin && (
                      <button
                        style={styles.convMenuBtn}
                        onClick={e => { e.stopPropagation(); setConvMenuId(menuOpen ? null : conv.id) }}
                        title="Tùy chọn"
                      >⋮</button>
                    )}
                  </div>
                </div>
                {menuOpen && (
                  <div style={styles.convDropdown} onClick={e => e.stopPropagation()}>
                    <div
                      style={{ ...styles.convDropdownItem, color: '#a78bfa' }}
                      onClick={() => openAutoReply(conv)}
                    >
                      ⚡ Auto Reply
                      {autoReplyMap[conv.id] && <span style={styles.autoReplyBadge}>Bật</span>}
                    </div>
                    <div style={styles.convDropdownItem} onClick={() => deleteConversation(conv.id)}>
                      🗑 Xóa đoạn chat
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </aside>

      {/* Khung chat */}
      {/* Main — trên mobile chỉ hiện khi đã chọn conversation */}
      <main style={{
        ...styles.main,
        ...(isMobile && !activeConv ? { display: 'none' } : {}),
      }}>
        {activeConv ? <>
          <div style={styles.chatHeader}>
            <div style={styles.chatHeaderInner}>
              {/* Nhóm trái: tên + trạng thái */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                {isMobile && (
                  <button
                    onClick={() => { setActiveConv(null); window.location.hash = '' }}
                    style={styles.backBtn}
                    title="Quay lại"
                  >←</button>
                )}
                <span style={styles.onlineDot(activeConv.partner?.is_online)} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  {editingMemo ? (
                    <form onSubmit={saveMemo} style={styles.memoForm}>
                      <input
                        ref={memoInputRef}
                        value={memoInput}
                        onChange={e => setMemoInput(e.target.value)}
                        placeholder="Tên gợi nhớ (bỏ trống = xóa)"
                        style={styles.memoInput}
                        onBlur={saveMemo}
                      />
                    </form>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <b style={{ fontSize: 15 }}>{partnerLabel(activeConv.partner)}</b>
                      {user?.is_admin && (
                        <button onClick={startEditMemo} style={styles.memoEditBtn} title="Đặt tên gợi nhớ">✏️</button>
                      )}
                    </div>
                  )}
                  <span style={styles.headerLastSeen}>{formatLastSeen(activeConv.partner)}</span>
                </div>
              </div>

              {/* Nhóm phải: gọi + menu */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                {callState ? (
                  <button style={styles.cancelRingBtn} onClick={hangUp}>
                    ⏹ {callState === 'active' ? 'Kết thúc' : 'Hủy gọi'}
                  </button>
                ) : (
                  <button style={styles.callBtn} onClick={callPartner} title="Gọi thoại">
                    📞
                  </button>
                )}
                <div style={{ position: 'relative' }}>
                  <button
                    style={styles.chatMenuBtn}
                    onClick={() => { setChatMenuOpen(v => !v); setAutoDeleteSub(false) }}
                    title="Tùy chọn"
                  >⋮</button>
                  {chatMenuOpen && (
                    <div style={styles.chatMenuDrop} onClick={e => e.stopPropagation()}>
                      <div style={styles.chatMenuItem} onClick={clearAllMessages}>
                        🗑 Xóa hết nội dung
                      </div>
                      <div
                        style={{ ...styles.chatMenuItem, justifyContent: 'space-between' }}
                        onClick={() => setAutoDeleteSub(v => !v)}
                      >
                        <span>
                          ⏱ Tự động xóa
                          {autoDeleteDays && <span style={styles.autoDeleteBadge}>{autoDeleteDays} ngày</span>}
                        </span>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{autoDeleteSub ? '▲' : '▶'}</span>
                      </div>
                      {autoDeleteSub && (
                        <div style={styles.autoDeleteSub}>
                          {[null, 1, 2].map(d => (
                            <div
                              key={String(d)}
                              style={{ ...styles.autoDeleteOption, ...(autoDeleteDays === d ? styles.autoDeleteOptionActive : {}) }}
                              onClick={() => changeAutoDelete(d)}
                            >
                              {d === null ? 'Tắt' : `Sau ${d} ngày`}
                              {autoDeleteDays === d && ' ✓'}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div style={styles.messages} onClick={() => { setChatMenuOpen(false); setAutoDeleteSub(false) }}>
            <div style={styles.msgInner}>
            {(() => {
              let lastLabel = null
              return messages.map(msg => {
                const label = formatDateLabel(msg.created_at)
                const showSep = label !== lastLabel
                lastLabel = label
                return (
                  <Fragment key={msg.id}>
                    {showSep && (
                      <div style={styles.dateSep}>
                        <span style={styles.dateSepText}>{label}</span>
                      </div>
                    )}
                    {(() => {
              // Tin nhắn hệ thống loại 'call' — hiển thị căn giữa
              if (msg.type === 'call') {
                const iCalled = msg.sender_id === user?.id  // true = tôi gọi đi
                let displayContent = msg.content
                if (msg.content.includes('Cuộc gọi nhỡ')) {
                  displayContent = iCalled ? '📵 Gọi đi · Không có người nghe' : '📵 Cuộc gọi nhỡ'
                } else if (msg.content.includes('bị từ chối')) {
                  displayContent = iCalled ? '🚫 Gọi đi · Bị từ chối' : '🚫 Cuộc gọi bị từ chối'
                }
                return (
                  <div key={msg.id} style={styles.callLog}>
                    <span style={styles.callLogText}>{displayContent}</span>
                    <span style={styles.callLogTime}>
                      {new Date(msg.created_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )
              }
              const isMe = msg.sender_id === user?.id
              const senderName = isMe ? myNameLabel : (msg.sender?.display_name || msg.sender?.username)
              const isRead = isMe && partnerLastReadAt && new Date(msg.created_at) <= new Date(partnerLastReadAt)
              const isHovered = hoveredMsg === msg.id
              return (
                <div
                  key={msg.id}
                  style={{ ...styles.msgRow, ...(isMe ? styles.msgRowMe : {}), position: 'relative' }}
                  onMouseEnter={() => setHoveredMsg(msg.id)}
                  onMouseLeave={() => setHoveredMsg(null)}
                >
                  {/* Menu hành động — hiện khi hover */}
                  {isHovered && !msg.is_recalled && (
                    <div style={{ ...styles.msgActions, ...(isMe ? styles.msgActionsMe : styles.msgActionsOther) }}>
                      {/* Reaction: Like */}
                      <button style={styles.msgActionBtn} onClick={() => toggleReaction(msg, '👍')} title="Thích">
                        👍
                      </button>
                      {/* Reaction: Angry */}
                      <button style={styles.msgActionBtn} onClick={() => toggleReaction(msg, '😡')} title="Tức giận">
                        😡
                      </button>
                      {/* Reply */}
                      <button style={styles.msgActionBtn} onClick={() => { setReplyTo(msg); chatInputRef.current?.focus() }} title="Trả lời">
                        ↩️
                      </button>
                      {/* Copy — chỉ text */}
                      {msg.type === 'text' && (
                        <button style={styles.msgActionBtn} onClick={() => copyText(msg.content)} title="Sao chép">
                          📋
                        </button>
                      )}
                      {/* Download — ảnh hoặc file */}
                      {(msg.type === 'image' || msg.type === 'file') && msg.file_url && (
                        <button style={styles.msgActionBtn} onClick={() => downloadFile(msg.file_url, msg.file_name)} title="Tải xuống">
                          ⬇
                        </button>
                      )}
                      {/* Chuyển tiếp — chỉ admin */}
                      {user?.is_admin && (
                        <button style={styles.msgActionBtn} onClick={() => setForwardingMsg(msg)} title="Chuyển tiếp">
                          ➡
                        </button>
                      )}
                      {/* Thu hồi — chỉ người gửi */}
                      {isMe && (
                        <button style={styles.msgActionBtn} onClick={() => recallMessage(msg)} title="Thu hồi (tất cả)">
                          ↩
                        </button>
                      )}
                      {/* Xóa cho mình */}
                      <button style={styles.msgActionBtn} onClick={() => deleteMessageForMe(msg)} title="Xóa (chỉ mình tôi)">
                        🗑
                      </button>
                    </div>
                  )}

                  <div style={{ ...styles.bubble, ...(isMe ? styles.bubbleMe : styles.bubbleOther), fontSize: chatFontSize }}>
                    {/* Chú thích admin: user đã xóa tin này khỏi phía họ */}
                    {msg.deleted_by_partner && user?.is_admin && (
                      <div style={styles.deletedByPartnerNote}>
                        🗑 {partnerLabel(activeConv?.partner)} đã xóa tin này
                      </div>
                    )}

                    {msg.is_recalled ? (
                      user?.is_admin ? (
                        // Admin thấy nội dung gốc + chú thích
                        <div>
                          {msg.type === 'image' && msg.file_url && (
                            <img src={api.fileUrl(msg.file_url)} alt={msg.file_name || 'ảnh'} style={styles.msgImage}
                              onClick={() => window.open(api.fileUrl(msg.file_url), '_blank')} />
                          )}
                          {msg.type === 'file' && msg.file_url && (
                            <a href={api.fileUrl(msg.file_url)} target="_blank" rel="noreferrer" style={styles.fileLink}>
                              📎 {msg.file_name || 'Tải file'}
                            </a>
                          )}
                          {msg.type === 'text' && <div>{renderText(msg.content)}</div>}
                          <div style={styles.recallNote}>⚠ {msg.sender?.display_name || msg.sender?.username} muốn thu hồi tin này</div>
                        </div>
                      ) : (
                        <div style={styles.recalledText}>Tin nhắn đã được thu hồi</div>
                      )
                    ) : (
                      <>
                        {/* Quote: tin nhắn đang reply */}
                        {msg.reply_to && (
                          <div style={{ ...styles.quoteBubble, ...(isMe ? styles.quoteBubbleMe : styles.quoteBubbleOther) }}>
                            <span style={styles.quoteName}>
                              {msg.reply_to.sender?.display_name || msg.reply_to.sender?.username}
                            </span>
                            <span style={styles.quoteText}>
                              {msg.reply_to.type === 'text'
                                ? msg.reply_to.content.slice(0, 80) + (msg.reply_to.content.length > 80 ? '…' : '')
                                : `📎 ${msg.reply_to.file_name || 'File'}`}
                            </span>
                          </div>
                        )}

                        {/* Ảnh */}
                        {msg.type === 'image' && msg.file_url && (
                          <img
                            src={api.fileUrl(msg.file_url)}
                            alt={msg.file_name || 'ảnh'}
                            style={styles.msgImage}
                            onClick={() => window.open(api.fileUrl(msg.file_url), '_blank')}
                          />
                        )}

                        {/* File */}
                        {msg.type === 'file' && msg.file_url && (
                          <a
                            href={api.fileUrl(msg.file_url)}
                            target="_blank"
                            rel="noreferrer"
                            style={styles.fileLink}
                          >
                            📎 {msg.file_name || 'Tải file'}
                          </a>
                        )}

                        {/* Text */}
                        {msg.type === 'text' && <div>{renderText(msg.content)}</div>}

                        {/* Reactions */}
                        {msg.reactions && msg.reactions.length > 0 && (() => {
                          const counts = msg.reactions.reduce((acc, r) => {
                            acc[r.emoji] = (acc[r.emoji] || 0) + 1
                            return acc
                          }, {})
                          return (
                            <div style={styles.reactionsRow}>
                              {Object.entries(counts).map(([emoji, count]) => (
                                <span
                                  key={emoji}
                                  style={{
                                    ...styles.reactionBadge,
                                    ...(msg.reactions.find(r => r.user_id === user?.id && r.emoji === emoji)
                                      ? styles.reactionBadgeActive : {})
                                  }}
                                  onClick={() => toggleReaction(msg, emoji)}
                                >
                                  {emoji} {count}
                                </span>
                              ))}
                            </div>
                          )
                        })()}
                      </>
                    )}

                    <div style={styles.msgMeta}>
                      <span style={styles.time}>
                        {new Date(msg.created_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {isMe && !msg.is_recalled && (
                        <span style={{ ...styles.tick, color: isRead ? '#a5f3fc' : 'rgba(255,255,255,0.5)' }}>
                          {isRead ? '✓✓' : '✓'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })()}
                  </Fragment>
                )
              })
            })()}
            <div ref={bottomRef} />
            </div>
          </div>

          {error && <div style={styles.errorBar}>{error}</div>}

          {/* Reply bar — hiện khi đang reply 1 tin */}
          {replyTo && (
            <div style={styles.replyBar}>
              <div style={styles.replyBarContent}>
                <span style={styles.replyBarLabel}>Trả lời</span>
                <span style={styles.replyBarName}>{replyTo.sender?.display_name || replyTo.sender?.username}</span>
                <span style={styles.replyBarText}>
                  {replyTo.type === 'text' ? replyTo.content : `📎 ${replyTo.file_name || 'File'}`}
                </span>
              </div>
              <button style={styles.replyBarClose} onClick={() => setReplyTo(null)}>✕</button>
            </div>
          )}

          <div style={styles.inputWrap}>
            <form onSubmit={sendMessage} style={styles.inputBar}>
              {/* Nút upload file/ảnh — dùng label để tương thích Capacitor Android */}
              <label style={{ ...styles.attachBtn, opacity: uploading ? 0.5 : 1, pointerEvents: uploading ? 'none' : 'auto', cursor: 'pointer' }} title="Gửi ảnh/file">
                {uploading ? '⏳' : '📎'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="*"
                  style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                  onChange={sendImage}
                  disabled={uploading}
                />
              </label>

              <input
                ref={chatInputRef}
                value={input}
                onChange={handleInputChange}
                onCompositionStart={() => { composingRef.current = true }}
                onCompositionEnd={e => { composingRef.current = false; setInput(e.target.value) }}
                placeholder={uploading ? 'Đang gửi file...' : 'Nhập tin nhắn...'}
                style={styles.textInput}
                disabled={uploading}
                autoFocus
              />
              <button
                type="submit"
                style={{ ...styles.sendBtn, ...(!input.trim() || uploading ? styles.sendBtnDisabled : {}) }}
                disabled={!input.trim() || uploading}
              >
                {uploading ? '⏳' : 'Gửi'}
              </button>
            </form>
          </div>
        </> : (
          <div style={styles.empty}>
            Chọn một hội thoại hoặc tìm username để bắt đầu chat
          </div>
        )}
      </main>


      {/* Overlay: cuộc gọi đến */}
      {incomingRing && (
        <div style={styles.ringOverlay}>
          <div style={styles.ringModal}>
            <div style={styles.ringIcon}>📞</div>
            <div style={styles.ringTitle}>{incomingRing.callerName}</div>
            <div style={styles.ringSubtitle}>đang gọi cho bạn</div>
            <div style={styles.ringActions}>
              <button style={styles.ringDeclineBtn} onClick={declineRing}>✕ Từ chối</button>
              <button style={styles.ringAcceptBtn} onClick={acceptRing}>✓ Chấp nhận</button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay cuộc gọi WebRTC */}
      {callState && (
        <div style={styles.callOverlay}>
          <div style={styles.callModal}>
            <div style={styles.callIcon}>{callState === 'active' ? '🎙' : '📞'}</div>
            <div style={styles.callName}>{callPartnerName}</div>
            <div style={styles.callStatus}>
              {callState === 'active' ? formatTimer(callTimer) : 'Đang kết nối...'}
            </div>
            <button style={styles.callHangupBtn} onClick={hangUp}>
              📵 {callState === 'active' ? 'Kết thúc' : 'Hủy'}
            </button>
            <audio ref={remoteAudioRef} autoPlay style={{ display: 'none' }} />
          </div>
        </div>
      )}


      {/* Modal Auto Reply */}
      {autoReplyConv && (
        <div style={styles.ringOverlay} onClick={() => setAutoReplyConv(null)}>
          <div style={styles.autoReplyModal} onClick={e => e.stopPropagation()}>
            <div style={styles.forwardTitle}>⚡ Auto Reply</div>
            <div style={{ fontSize: 13, color: '#9ca3af' }}>
              Tự động trả lời mỗi tin của <b style={{ color: '#e2e8f0' }}>{partnerLabel(autoReplyConv.partner) || 'user'}</b>
            </div>

            <label style={styles.autoReplyToggleRow}>
              <span style={{ fontSize: 13, color: '#e2e8f0' }}>Bật Auto Reply</span>
              <div
                style={{ ...styles.toggleTrack, background: autoReplyActive ? '#4f46e5' : '#374151' }}
                onClick={() => setAutoReplyActive(v => !v)}
              >
                <div style={{ ...styles.toggleThumb, transform: autoReplyActive ? 'translateX(20px)' : 'translateX(0)' }} />
              </div>
            </label>

            <div style={{ fontSize: 12, color: '#9ca3af' }}>Nội dung trả lời:</div>
            <textarea
              value={autoReplyContent}
              onChange={e => setAutoReplyContent(e.target.value)}
              rows={3}
              style={styles.autoReplyTextarea}
              placeholder="Ví dụ: Ok, cảm ơn bạn!"
              disabled={!autoReplyActive}
            />

            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.forwardCancelBtn} onClick={() => setAutoReplyConv(null)}>Hủy</button>
              <button
                style={{ ...styles.approveBtn, flex: 1, padding: '8px', fontSize: 13 }}
                onClick={saveAutoReply}
                disabled={autoReplyActive && !autoReplyContent.trim()}
              >Lưu</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal chuyển tiếp tin nhắn (admin) */}
      {forwardingMsg && (
        <div style={styles.ringOverlay} onClick={() => setForwardingMsg(null)}>
          <div style={styles.forwardModal} onClick={e => e.stopPropagation()}>
            <div style={styles.forwardTitle}>➡ Chuyển tiếp tới</div>
            <div style={styles.forwardPreview}>
              {forwardingMsg.type === 'text'
                ? `"${forwardingMsg.content.slice(0, 60)}${forwardingMsg.content.length > 60 ? '...' : ''}"`
                : `📎 ${forwardingMsg.file_name || 'File'}`}
            </div>
            <div style={styles.forwardList}>
              {conversations
                .filter(c => c.id !== activeConv?.id)
                .map(c => (
                  <div key={c.id} style={styles.forwardItem} onClick={() => forwardMessage(c.id)}>
                    <span style={styles.onlineDot(c.partner?.is_online)} />
                    <span>{partnerLabel(c.partner) || c.name || 'Chat'}</span>
                  </div>
                ))}
              {conversations.filter(c => c.id !== activeConv?.id).length === 0 && (
                <div style={styles.forwardEmpty}>Không có hội thoại nào khác</div>
              )}
            </div>
            <button style={styles.forwardCancelBtn} onClick={() => setForwardingMsg(null)}>Hủy</button>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  layout: { display: 'flex', height: '100vh', fontFamily: 'sans-serif', background: '#f0f2f5' },
  sidebar: { width: 338, background: '#1a1a2e', color: '#fff', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarHeader: { padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #2d2d44' },
  appName: { fontWeight: 700, fontSize: 18 },
  logoutBtn: { background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, display: 'flex', alignItems: 'center', transition: 'color 0.15s, background 0.15s' },
  me: { padding: '8px 16px', fontSize: 13, color: '#aaa', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' },
  myNameInput: { background: '#2d2d44', color: '#fff', border: '1px solid #4f46e5', borderRadius: 4, padding: '2px 6px', fontSize: 13, outline: 'none', width: 120 },
  myNameEditBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: '0 2px', opacity: 0.5 },
  searchWrap: { position: 'relative', padding: '8px 16px' },
  newDmInput: { width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 6, border: 'none', fontSize: 12, background: '#2d2d44', color: '#fff', outline: 'none' },
  searchDrop: { position: 'absolute', left: 16, right: 16, background: '#2d2d44', borderRadius: 6, zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', overflow: 'hidden' },
  searchItem: { padding: '8px 12px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, borderBottom: '1px solid #1a1a2e' },
  searchName: { fontSize: 13, fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: 6 },
  adminBadge: { fontSize: 9, background: '#4f46e5', color: '#fff', borderRadius: 4, padding: '1px 5px', fontWeight: 700, letterSpacing: 0.5 },
  searchSub: { fontSize: 11, color: '#888' },
  pendingSection: { borderBottom: '1px solid #2d2d44' },
  pendingToggle: { width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  pendingCount: { background: '#f59e0b', color: '#000', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 700 },
  pendingEmpty: { padding: '8px 16px', fontSize: 12, color: '#555' },
  pendingItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 16px', borderTop: '1px solid #2d2d44' },
  pendingName: { fontSize: 13, fontWeight: 600, color: '#fff' },
  pendingSub: { fontSize: 11, color: '#888' },
  pendingActions: { display: 'flex', gap: 6 },
  approveBtn: { padding: '4px 8px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700 },
  rejectBtn: { padding: '4px 8px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 700 },
  convList: { flex: 1, overflowY: 'auto' },
  convItem: { padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid #2d2d44' },
  convItemActive: { background: '#2d2d44' },
  convRow: { display: 'flex', alignItems: 'center', gap: 12 },
  convRowRight: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  convAvatar: { width: 46, height: 46, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0, userSelect: 'none' },
  onlineDotAvatar: { position: 'absolute', bottom: 1, right: 1, width: 11, height: 11, borderRadius: '50%', border: '2px solid #1a1a2e' },
  convMenuBtn: { background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 16, padding: '0 2px', lineHeight: 1 },
  convDropdown: { position: 'absolute', right: 8, top: '100%', background: '#2d2d44', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.4)', zIndex: 50, minWidth: 150, overflow: 'hidden' },
  convDropdownItem: { padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 },
  autoReplyBadge: { marginLeft: 'auto', fontSize: 10, background: '#4f46e5', color: '#fff', borderRadius: 4, padding: '1px 5px' },
  autoReplyDot: { fontSize: 10, lineHeight: 1, flexShrink: 0 },
  autoReplyModal: { background: '#1a1a2e', color: '#fff', borderRadius: 16, padding: '24px 28px', minWidth: 300, maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' },
  autoReplyToggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' },
  toggleTrack: { width: 44, height: 24, borderRadius: 12, position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 },
  toggleThumb: { position: 'absolute', top: 3, left: 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'transform 0.2s' },
  autoReplyTextarea: { background: '#2d2d44', color: '#e2e8f0', border: '1px solid #3d3d5c', borderRadius: 8, padding: '8px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', outline: 'none' },
  quoteLine: { display: 'flex', alignItems: 'stretch', gap: 8, marginBottom: 4 },
  quoteBar: { width: 3, borderRadius: 2, background: '#6b7280', flexShrink: 0 },
  quoteText: { color: '#9ca3af', fontSize: '0.92em', fontStyle: 'italic', wordBreak: 'break-word' },
  convNameRow: { display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2, width: '100%' },
  convTime: { fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' },
  onlineDot: (isOnline) => ({
    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
    background: isOnline ? '#22c55e' : '#4b5563',
    display: 'inline-block',
  }),
  convName: { fontWeight: 600, fontSize: 14 },
  unreadBadge: { fontSize: 11, background: '#4f46e5', color: '#fff', borderRadius: 10, padding: '1px 7px', fontWeight: 700, minWidth: 18, textAlign: 'center' },
  convPreview: { fontSize: 12, color: '#888', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  chatHeader: { padding: '14px 16px', background: '#e8eaf6', borderBottom: '1px solid #c5cae9', fontSize: 15 },
  chatHeaderInner: { maxWidth: 720, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  backBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#4f46e5', fontWeight: 700, padding: '0 8px 0 0', lineHeight: 1, flexShrink: 0 },
  memoForm: { display: 'flex' },
  memoInput: { padding: '4px 8px', borderRadius: 6, border: '1px solid #4f46e5', fontSize: 14, outline: 'none', minWidth: 180 },
  memoEditBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '0 2px', opacity: 0.5 },
  headerVs: { color: '#9ca3af', margin: '0 4px', fontWeight: 400 },
  onlineLabel: { fontSize: 12, color: '#22c55e', marginLeft: 2 },
  headerLastSeen: { fontSize: 12, color: '#6b7280' },
  messages: { flex: 1, overflowY: 'auto', padding: '0 16px' },
  msgInner: { maxWidth: 720, margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 0' },
  msgRow: { display: 'flex' },
  msgRowMe: { justifyContent: 'flex-end' },
  bubble: { maxWidth: '75%', padding: '8px 12px', borderRadius: 12, fontSize: 14, lineHeight: 1.5 },
  bubbleOther: { background: '#fff', borderTopLeftRadius: 2 },
  bubbleMe: { background: '#4f46e5', color: '#fff', borderTopRightRadius: 2 },
  senderName: { fontSize: 11, fontWeight: 700, marginBottom: 2, color: '#4f46e5' },
  senderNameMe: { color: 'rgba(255,255,255,0.75)' },
  msgImage: { maxWidth: '100%', maxHeight: 300, borderRadius: 8, display: 'block', cursor: 'pointer', marginTop: 4 },
  fileLink: { display: 'block', color: '#a5f3fc', textDecoration: 'underline', marginTop: 4, fontSize: 13 },
  msgMeta: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4, marginTop: 4 },
  time: { fontSize: 10, opacity: 0.6 },
  tick: { fontSize: 12, fontWeight: 700 },
  errorBar: { padding: '8px 20px', background: '#fee2e2', color: '#b91c1c', fontSize: 13 },
  inputBar: { display: 'flex', padding: '12px 0', background: 'transparent', gap: 10, alignItems: 'center', maxWidth: 720, margin: '0 auto' },
  attachBtn: { position: 'relative', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: '0 4px', color: '#6b7280', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none' },
  textInput: { flex: 1, padding: '10px 14px', borderRadius: 24, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none' },
  inputWrap: { borderTop: '1px solid #c5cae9', padding: '0 16px', background: '#e8eaf6' },
  sendBtn: { padding: '10px 20px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 24, fontWeight: 600, cursor: 'pointer', transition: 'opacity 0.2s' },
  sendBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  empty: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 15 },
  callBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: '0 4px' },
  chatMenuBtn: { marginLeft: 'auto', background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', padding: '0 4px', color: '#6b7280', lineHeight: 1, fontWeight: 700 },
  chatMenuDrop: { position: 'absolute', right: 0, top: '110%', background: '#fff', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 100, minWidth: 200, overflow: 'hidden', border: '1px solid #e5e7eb' },
  chatMenuItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', cursor: 'pointer', fontSize: 14, color: '#374151', borderBottom: '1px solid #f3f4f6' },
  autoDeleteBadge: { marginLeft: 6, fontSize: 11, background: '#4f46e5', color: '#fff', borderRadius: 8, padding: '1px 7px' },
  autoDeleteSub: { background: '#f9fafb', borderTop: '1px solid #f3f4f6' },
  autoDeleteOption: { padding: '9px 28px', cursor: 'pointer', fontSize: 13, color: '#6b7280' },
  autoDeleteOptionActive: { color: '#4f46e5', fontWeight: 600 },
  cancelRingBtn: { marginLeft: 'auto', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  ringOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  ringModal: { background: '#1a1a2e', color: '#fff', borderRadius: 20, padding: '40px 48px', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', minWidth: 280 },
  ringIcon: { fontSize: 56, marginBottom: 16, animation: 'none' },
  ringTitle: { fontSize: 22, fontWeight: 700, marginBottom: 6 },
  ringSubtitle: { fontSize: 14, color: '#aaa', marginBottom: 32 },
  ringActions: { display: 'flex', gap: 16, justifyContent: 'center' },
  ringDeclineBtn: { padding: '12px 28px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 50, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  ringAcceptBtn: { padding: '12px 28px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 50, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  callOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 },
  callModal: { background: '#1a1a2e', color: '#fff', borderRadius: 24, padding: '48px 56px', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', minWidth: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  callIcon: { fontSize: 56, marginBottom: 4 },
  callName: { fontSize: 20, fontWeight: 700 },
  callStatus: { fontSize: 14, color: '#aaa', marginBottom: 8, fontVariantNumeric: 'tabular-nums' },
  callHangupBtn: { padding: '12px 32px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 50, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 4 },
  deviceSectionLabel: { padding: '6px 16px', fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, borderTop: '1px solid #2d2d44' },
  otpItem: { padding: '8px 16px', borderTop: '1px solid #2d2d44', background: '#16162a' },
  otpItemBody: { display: 'flex', flexDirection: 'column', gap: 2 },
  otpItemCode: { fontSize: 24, fontWeight: 900, letterSpacing: 6, color: '#fbbf24', fontFamily: 'monospace', margin: '4px 0 2px' },
  otpItemHint: { fontSize: 10, color: '#6b7280', fontStyle: 'italic' },
  forwardModal: { background: '#1a1a2e', color: '#fff', borderRadius: 16, padding: '24px 28px', minWidth: 280, maxWidth: 360, maxHeight: '70vh', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' },
  forwardTitle: { fontSize: 16, fontWeight: 700 },
  forwardPreview: { fontSize: 13, color: '#aaa', background: '#2d2d44', borderRadius: 8, padding: '8px 12px', wordBreak: 'break-word' },
  forwardList: { overflowY: 'auto', maxHeight: 300, display: 'flex', flexDirection: 'column', gap: 2 },
  forwardItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, background: '#2d2d44', transition: 'background 0.15s' },
  forwardEmpty: { fontSize: 13, color: '#555', textAlign: 'center', padding: '16px 0' },
  forwardCancelBtn: { alignSelf: 'flex-end', padding: '8px 18px', background: '#374151', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  msgActions: { position: 'absolute', top: 0, display: 'flex', gap: 4, zIndex: 10 },
  msgActionsMe: { right: 0, transform: 'translateY(-100%)' },
  msgActionsOther: { left: 0, transform: 'translateY(-100%)' },
  msgActionBtn: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 7px', cursor: 'pointer', fontSize: 13, boxShadow: '0 1px 4px rgba(0,0,0,0.12)' },
  fontPickerDrop: { position: 'absolute', right: 0, top: '110%', background: '#1a1a2e', border: '1px solid #2d2d44', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.5)', zIndex: 200, minWidth: 130, overflow: 'hidden' },
  fontPickerBtn: { width: '100%', padding: '9px 14px', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'baseline', gap: 4, transition: 'background 0.12s' },
  dateSep: { display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '8px 0' },
  dateSepText: { fontSize: 11, color: '#9ca3af', background: '#e5e7eb', borderRadius: 10, padding: '2px 12px', fontWeight: 500 },
  callLog: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, margin: '4px 0' },
  callLogText: { fontSize: 12, color: '#6b7280', background: '#f3f4f6', borderRadius: 10, padding: '4px 14px', fontWeight: 500 },
  callLogTime: { fontSize: 10, color: '#9ca3af' },
  deletedByPartnerNote: { fontSize: 10, color: '#f59e0b', fontStyle: 'italic', marginBottom: 4 },
  recalledText: { fontStyle: 'italic', opacity: 0.55, fontSize: 13 },
  recallNote: { marginTop: 4, fontSize: 11, color: '#f59e0b', fontStyle: 'italic' },
  // Reply bar trên input
  replyBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#f0f0f8', borderTop: '1px solid #e5e7eb' },
  replyBarContent: { flex: 1, display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden' },
  replyBarLabel: { fontSize: 10, color: '#4f46e5', fontWeight: 600, textTransform: 'uppercase' },
  replyBarName: { fontSize: 11, color: '#374151', fontWeight: 600 },
  replyBarText: { fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  replyBarClose: { background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: 4, flexShrink: 0 },
  // Quote block trong bubble
  quoteBubble: { borderRadius: 6, padding: '4px 8px', marginBottom: 5, display: 'flex', flexDirection: 'column', gap: 1, cursor: 'default' },
  quoteBubbleMe: { background: 'rgba(255,255,255,0.2)', borderLeft: '3px solid rgba(255,255,255,0.7)' },
  quoteBubbleOther: { background: 'rgba(0,0,0,0.06)', borderLeft: '3px solid #4f46e5' },
  quoteName: { fontSize: 10, fontWeight: 700, color: '#4f46e5' },
  quoteText: { fontSize: 11, opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  // Reactions
  reactionsRow: { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 },
  reactionBadge: { display: 'inline-flex', alignItems: 'center', gap: 2, background: 'rgba(0,0,0,0.07)', borderRadius: 12, padding: '1px 7px', fontSize: 13, cursor: 'pointer', userSelect: 'none', border: '1.5px solid transparent' },
  reactionBadgeActive: { background: 'rgba(79,70,229,0.15)', border: '1.5px solid #4f46e5' },
}
